import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

interface IdentityMediaPagerProps {
  pagination: {
    label: string
    page: number
    pageSize: number
    total: number
    loading?: boolean
  }
  onPageChange: (page: number) => void
}

/** Quiet page controls shared by confirmed faces and full photos. */
export function IdentityMediaPager({ pagination, onPageChange }: IdentityMediaPagerProps) {
  const { t } = useTranslation()
  const { label, page, pageSize, total, loading = false } = pagination
  const totalPages = Math.ceil(total / pageSize)
  if (totalPages <= 1) return null

  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)

  return (
    <nav aria-label={label} className="flex flex-wrap items-center justify-between gap-3 pt-2">
      <span aria-live="polite" className="text-xs tabular-nums text-muted-foreground">
        <span aria-hidden="true">
          {first}–{last} / {total}
        </span>
        <span className="sr-only">
          {t('identity.unassigned.pageOf', { page, total: totalPages })}
        </span>
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          {t('identity.unassigned.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={loading || page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          {t('identity.unassigned.next')}
        </Button>
      </div>
    </nav>
  )
}
